import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Modal,
  Button,
  Form,
  Table,
  Alert,
  Spinner,
  Card,
} from 'react-bootstrap';
import axios from 'axios';
import appConstants from '../constants/appConstants';
import Layout from '../components/Layout';
import { FaPlus, FaEdit, FaTrash, FaArrowLeft, FaListUl } from 'react-icons/fa';

const getAuthToken = () => localStorage.getItem('authToken');

const FeeSlabs = () => {
  const { feeStructureId } = useParams();
  const navigate = useNavigate();
  const [feeSlabs, setFeeSlabs] = useState([]);
  const [feeStructure, setFeeStructure] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    from_date: '',
    to_date: '',
    tuition_discount: '',
    effective_tuition_fee: '',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });

  useEffect(() => {
    fetchFeeStructure();
    fetchFeeSlabs();
  }, [feeStructureId]);

  const fetchFeeStructure = async () => {
    const token = getAuthToken();
    if (!token) {
      showAlert('Unauthorized! Please log in.', 'danger');
      return;
    }

    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}${appConstants.FEE_STRUCTURE.GET}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { id: feeStructureId },
        },
      );

      if (response.data.status === 'success') {
        setFeeStructure(response.data.data);
      } else {
        showAlert('Failed to fetch fee structure details', 'danger');
      }
    } catch (error) {
      console.error('Error fetching fee structure:', error);
      showAlert('Error loading fee structure details', 'danger');
    }
  };

  const fetchFeeSlabs = async () => {
    const token = getAuthToken();
    if (!token) {
      showAlert('Unauthorized! Please log in.', 'danger');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}${appConstants.FEE_SLAB.LIST}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { fee_structure_id: feeStructureId },
        },
      );

      if (response.data.status === 'success') {
        setFeeSlabs(response.data.data || []);
      } else {
        showAlert('Failed to fetch fee slabs', 'danger');
      }
    } catch (error) {
      console.error('Error fetching fee slabs:', error);
      const errorMessage =
        error.response?.data?.message || 'Error loading fee slabs';
      showAlert(errorMessage, 'danger');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const token = getAuthToken();
    if (!token) {
      showAlert('Unauthorized! Please log in.', 'danger');
      return;
    }

    if (
      !formData.from_date ||
      !formData.to_date ||
      formData.tuition_discount === '' ||
      !formData.effective_tuition_fee
    ) {
      showAlert('Please fill in all required fields', 'warning');
      return;
    }

    setSaving(true);
    try {
      const endpoint = formData.id
        ? appConstants.FEE_SLAB.UPDATE
        : appConstants.FEE_SLAB.CREATE;

      const payload = {
        fee_structure_id: parseInt(feeStructureId),
        from_date: formData.from_date,
        to_date: formData.to_date,
        tuition_discount: parseFloat(formData.tuition_discount),
        effective_tuition_fee: parseFloat(formData.effective_tuition_fee),
      };

      if (formData.id) {
        payload.id = formData.id;
      }

      const response = await axios.post(
        `${appConstants.API_ROOT_URL}${endpoint}`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.status === 'success') {
        showAlert(
          `Fee slab ${formData.id ? 'updated' : 'created'} successfully!`,
          'success',
        );
        setShowModal(false);
        resetForm();
        fetchFeeSlabs();
      } else {
        showAlert(response.data.message || 'Failed to save fee slab', 'danger');
      }
    } catch (error) {
      console.error('Error saving fee slab:', error);
      const errorMessage =
        error.response?.data?.message || 'Error saving fee slab';
      showAlert(errorMessage, 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (feeSlab) => {
    setFormData({
      id: feeSlab.id,
      from_date: feeSlab.from_date || '',
      to_date: feeSlab.to_date || '',
      tuition_discount: feeSlab.tuition_discount || '',
      effective_tuition_fee: feeSlab.effective_tuition_fee || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this fee slab?')) {
      return;
    }

    const token = getAuthToken();
    if (!token) {
      showAlert('Unauthorized! Please log in.', 'danger');
      return;
    }

    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}${appConstants.FEE_SLAB.DELETE}`,
        { id: id },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.status === 'success') {
        showAlert('Fee slab deleted successfully!', 'success');
        fetchFeeSlabs();
      } else {
        showAlert(
          response.data.message || 'Failed to delete fee slab',
          'danger',
        );
      }
    } catch (error) {
      console.error('Error deleting fee slab:', error);
      const errorMessage =
        error.response?.data?.message || 'Error deleting fee slab';
      showAlert(errorMessage, 'danger');
    }
  };

  const showAlert = (message, variant) => {
    setAlert({ show: true, message, variant });
    setTimeout(() => setAlert({ show: false, message: '', variant: '' }), 5000);
  };

  const resetForm = () => {
    setFormData({
      id: null,
      from_date: '',
      to_date: '',
      tuition_discount: '',
      effective_tuition_fee: '',
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  return (
    <Layout>
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <Button
              variant="outline-secondary"
              onClick={() => navigate('/fees-structure')}
              className="me-3"
            >
              <FaArrowLeft className="me-2" />
              Back to Fee Structures
            </Button>
            <h4 className="d-inline">Fee Slabs Management</h4>
          </div>
          <Button
            variant="primary"
            onClick={() => setShowModal(true)}
            style={{ backgroundColor: '#0657CE' }}
          >
            <FaPlus className="me-2" />
            Add Fee Slab
          </Button>
        </div>

        {/* Fee Structure Info Card */}
        {feeStructure && (
          <Card className="mb-3">
            <Card.Body>
              <Card.Title>{feeStructure.name}</Card.Title>
              <div className="row">
                <div className="col-md-3">
                  <strong>Tuition Fee:</strong> ₹
                  {parseFloat(feeStructure.tuition_fee || 0).toLocaleString()}
                </div>
                <div className="col-md-3">
                  <strong>Admission Fee:</strong> ₹
                  {parseFloat(feeStructure.admission_fee || 0).toLocaleString()}
                </div>
                <div className="col-md-6">
                  <strong>Course Period:</strong>{' '}
                  {feeStructure.course_start_date} to{' '}
                  {feeStructure.course_end_date}
                </div>
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Floating Alert (bottom left) */}
        {alert.show && (
          <div
            style={{
              position: 'fixed',
              left: '18px',
              bottom: '18px',
              zIndex: 9999,
              minWidth: '180px',
              maxWidth: '320px',
              pointerEvents: 'auto',
              boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
            }}
          >
            <div
              style={{
                position: 'relative',
                borderRadius: '6px',
                background:
                  alert.variant === 'success'
                    ? '#e6f7e6'
                    : alert.variant === 'danger'
                      ? '#ffeaea'
                      : '#fff8e1',
                color: '#5c4200',
                marginBottom: 0,
                fontWeight: 500,
                fontSize: '0.92rem',
                padding: '6px 32px 6px 12px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                minHeight: '32px',
                display: 'flex',
                alignItems: 'center',
                maxWidth: '320px',
              }}
            >
              <span style={{ flex: 1 }}>{alert.message}</span>
              <button
                onClick={() =>
                  setAlert({ show: false, message: '', variant: '' })
                }
                style={{
                  position: 'absolute',
                  top: '50%',
                  right: '8px',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#5c4200',
                  fontWeight: 700,
                  fontSize: '1.1em',
                  cursor: 'pointer',
                  padding: 0,
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Fee Slabs Table */}
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : (
          <Table striped bordered hover responsive>
            <thead style={{ backgroundColor: '#0657CE', color: 'white' }}>
              <tr>
                <th>From Date</th>
                <th>To Date</th>
                <th>Tuition Discount</th>
                <th>Effective Tuition Fee</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {feeSlabs.length > 0 ? (
                feeSlabs.map((feeSlab) => (
                  <tr key={feeSlab.id}>
                    <td>{feeSlab.from_date}</td>
                    <td>{feeSlab.to_date}</td>
                    <td>
                      ₹
                      {parseFloat(
                        feeSlab.tuition_discount || 0,
                      ).toLocaleString()}
                    </td>
                    <td>
                      ₹
                      {parseFloat(
                        feeSlab.effective_tuition_fee || 0,
                      ).toLocaleString()}
                    </td>
                    <td>
                      <Button
                        variant="outline-info"
                        size="sm"
                        className="me-2"
                        onClick={() => navigate(`/installments/${feeSlab.id}`)}
                        title="Manage Installments"
                      >
                        <FaListUl />
                      </Button>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        onClick={() => handleEdit(feeSlab)}
                      >
                        <FaEdit />
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(feeSlab.id)}
                      >
                        <FaTrash />
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center">
                    No fee slabs found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {formData.id ? 'Edit Fee Slab' : 'Add New Fee Slab'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>From Date *</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.from_date}
                    onChange={(e) =>
                      setFormData({ ...formData, from_date: e.target.value })
                    }
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>To Date *</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.to_date}
                    onChange={(e) =>
                      setFormData({ ...formData, to_date: e.target.value })
                    }
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Tuition Discount *</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Enter tuition discount amount"
                    value={formData.tuition_discount}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        tuition_discount: e.target.value,
                      })
                    }
                    min="0"
                    step="0.01"
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Effective Tuition Fee *</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Enter effective tuition fee"
                    value={formData.effective_tuition_fee}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        effective_tuition_fee: e.target.value,
                      })
                    }
                    min="0"
                    step="0.01"
                  />
                </Form.Group>
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving}
            style={{ backgroundColor: '#0657CE' }}
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              <>{formData.id ? 'Update' : 'Create'}</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
};

export default FeeSlabs;
